import React from 'react';

type Props<P> = {
  as?: React.ComponentType<P> | React.ElementType;
  column?: string | number;
  row?: string | number;
  area?: string;
  width?: number;
  height?: number;
  place?: string;
  children?: React.ReactNode;
} & Partial<P>;

/**
 * Component that exposes some useful grid placement CSS rules as props.
 * Supports rendering as another component or element via the `as` prop, and is typed
 * to accept that component's props as well, though the component MUST support the
 * `style` prop in order for GridItem to work.
 *
 * The `column` and `row` props accept a string or number and translate directly to the
 * CSS grid-column and grid-row rules. The css-tricks guide is a good resource on usage:
 * https://css-tricks.com/snippets/css/complete-guide-grid/#grid-columngrid-row
 *
 * The `width` and `height` props accept a number which is translated into a column
 * or row span, respectively.
 *
 * The `place` prop translates to the `place-self` CSS rule:
 * https://css-tricks.com/snippets/css/complete-guide-grid/#place-self
 *
 * ### Example
 *
 * ```tsx
 * <Grid rows={3} columns={4}>
 *   <GridItem row="2 / 3" column={2} width={2} place="center end">
 *     XXX
 *   </GridItem>
 * </Grid>
 * ```
 *
 * Result:
 * ```
 * +--+--+--+--+
 * |  |  |  |  |
 * +--+-----+--+
 * |  |     |  |
 * +--+  XXX|--+
 * |  |     |  |
 * +--+--+--+--+
 * ```
 */
export default function GridItem<P>({
  as: Component = 'div',
  column,
  row,
  area,
  width,
  height,
  place,
  ...props
}: Props<P>) {
  return (
    <Component
      style={{
        gridColumn: column,
        ...(width && { gridColumnEnd: `span ${width}` }),
        gridRow: row,
        ...(height && { gridRowEnd: `span ${height}` }),
        ...(area && { gridArea: area }),
        placeSelf: place,
      }}
      {...props}
    />
  );
}
